import { urlGenerator } from "app";

import { getProviderType, fetchJson, getListData, createResponse, logMethod } from "./dataProvider.utils";
import { LEGACY, MIXED, PASCAL } from "./dataProvider.types";

const dataProvider = {
  customRequest: async (url, data, method = "GET", params) => {
    params ? (url = `${urlGenerator(url)}${params}`) : (url = `${urlGenerator(url)}`);

    logMethod("customRequest", url);

    return fetchJson({ url, method, data }).then(response => {
      return { data: response.json || response.body };
    });
  },

  getList: async (resource, params = {}) => {
    let url = `${urlGenerator(resource)}`;
    let firstParam = false;

    if (params.pagination) {
      const { page, perPage } = params.pagination;
      firstParam = true;

      switch (getProviderType(params)) {
        case LEGACY:
          url = `${url}?_start=${perPage * (page - 1)}&_end=${perPage * page}`;
          break;
        case MIXED:
        case PASCAL:
          url = `${url}?CurrentPage=${page}&PerPage=${perPage}`;
          break;
        default:
          throw new Error("Not implemented operation");
      }
    }

    if (params.filter) {
      const filters = Object.keys(params.filter)
        .map(filterKey => {
          if (Array.isArray(params.filter[filterKey])) {
            return params.filter[filterKey].map(filterValue => `&${filterKey}=${filterValue}`).join("");
          } else {
            return `&${filterKey}=${params.filter[filterKey]}`;
          }
        })
        .join("");

      if (filters) {
        url = `${url}${filters}`;
        firstParam = true;
      }
    }

    const query = params.query || params.meta?.query;

    if (query) {
      url = `${url}${firstParam ? "&" : "?"}${query}`;
    }

    if (params.sort) {
      url = `${url}&SortBy=${params.sort.field}&SortOrder=${params.sort.order}`;
    }

    logMethod("getList", url);
    const response = await fetchJson({ url, method: "GET", params });

    return getListData({ response, params });
  },

  getOne: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/${params.id ? params.id : ""}`;

    logMethod("getOne", url);

    return fetchJson({ url, method: "GET", params }).then(response => {
      const body = response.json;

      if (body.id === undefined) {
        body.id = body.Id;
      }

      return {
        data: body,
      };
    });
  },

  getMany: (resource, params = {}) => {
    let url = `${urlGenerator(resource)}`;
    if (params.ids) {
      url = `${url}?${params.ids.map(q => `ids=${q}`).join("&")}&PerPage=${params.ids?.length || 1}`;
    }

    logMethod("getMany", url);

    return fetchJson({ url, method: "GET", params }).then(response => {
      return getListData({ response, params });
    });
  },

  delete: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/${params.id}`;

    logMethod("delete", url);

    const response = await fetchJson({ url, method: "DELETE", params });
    return createResponse(response);
  },

  deleteMany: async (resource, params = {}) => {
    logMethod("deleteMany");

    if (params.ids && params.ids.length > 0) {
      let url = `${urlGenerator(resource)}`;

      params.ids.forEach(async id => {
        await fetchJson({ url: `${url}/${id}`, method: "DELETE", params });
      });
    }

    return { data: params.ids || [] };
  },

  create: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}`;
    const data = params.data;

    logMethod("create", url);

    const response = await fetchJson({ url, method: "POST", data, params });
    return createResponse(response);
  },

  update: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/${params.id}${params.meta?.suffix ? `/${params.meta.suffix}` : ""}`;

    logMethod("update", url);

    const data = params.meta?.suffix ? { ...params.data, ...params.meta.params } : params.data;
    const response = await fetchJson({ url, method: "PUT", data, params });
    return createResponse(response);
  },

  getTree: async (resource, params) => {
    const url = `${urlGenerator(resource)}/tree`;

    logMethod("getTree", url);

    const response = await fetchJson({ url, method: "GET" });
    return { data: response.json };
  },

  moveAsNthChildOf: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/move-child`;

    logMethod("moveAsNthChildOf", url);

    await fetchJson({
      url,
      method: "PUT",
      data: {
        destinationId: params.destination.id,
        position: params.position,
        sourceId: params.source.id,
      },
      params: {
        meta: { useFormData: false },
      },
    });
    return { data: {} };
  },

  moveAsNthSiblingOf: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/move-sibling`;

    logMethod("moveAsNthSiblingOf", url);

    await fetchJson({
      url,
      method: "PUT",
      data: {
        destinationId: params.destination.id,
        position: params.position,
        sourceId: params.source.id,
      },
      params: {
        meta: { useFormData: false },
      },
    });
    return { data: {} };
  },

  addRootNode: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/root`;

    logMethod("addRootNode", url);

    const response = await fetchJson({
      url,
      method: "POST",
      data: {
        kvp: {
          ...params.data,
          isRoot: true,
        },
      },
      params: {
        meta: { useFormData: false },
      },
    });

    return { data: response.json };
  },

  addChildNode: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/child`;

    logMethod("addChildNode", url);

    const response = await fetchJson({
      url,
      method: "POST",
      data: {
        parentId: params.parentId,
        TreeData: params.data,
      },
      params: {
        meta: { useFormData: false },
      },
    });
    return { data: response.json };
  },

  deleteBranch: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/${params.id}`;

    logMethod("deleteBranch", url);

    await fetchJson({
      url,
      method: "DELETE",
    });

    return { data: {} };
  },
};

export default dataProvider;
